import React, { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import LessonSidebar from "../../../../components/Content/LessonSidebar";
import axiosInstance from "../../../../utils/config/config";
import ConfirmationDialog from "../../../../components/Content/ConfirmationDialog";
import {
  ConfirmationSnackbar,
  ErrorSnackbar,
} from "../../../../components/Common/CustomSnackbar";
import { useQuickLinksItems } from "../../../../context/QuickLinksContext";
import { getUserById } from "../../../../redux/actions/user.dispatcher";
import {
  DIALOG_TITLE,
  DIALOG_CONTENT,
  ACCESS_LINK_TEXT,
  CHECKBOX_LABEL,
  SUCCESS_MESSAGE,
  ERROR_ENROLL_MESSAGE,
  SECTION_HEADING,
} from "./LessonDashboard.constants";
import {
  RootBox,
  HeaderStack,
  Heading,
  AccessLink,
  CheckboxWrapper,
  StyledCheckbox,
  CheckboxIconUnchecked,
  CheckboxIconChecked,
  CheckboxLabel,
  ContentBox,
  ContentText,
  StyledBackdrop,
  ContentGrid,
} from "./LessonDashboard.styles";

const LessonDashboard = ({ lessonsData, setCompState }) => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.user);
  const { lessonContent, setLessonContent } = useQuickLinksItems();

  const [dialogState, setDialogState] = useState(false);
  const [startingLesson, setStartingLesson] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState({
    error: false,
    message: "",
  });
  const [success, setSuccess] = useState(false);
  const [todaysLesson, setTodaysLesson] = useState(null);
  const [lessonOnDashboard, setLessonOnDashboard] = useState(
    userData?.showAcimLesson || false
  );

  const getTodaysLesson = async () => {
    try {
      const response = await axiosInstance.get("/courselessons/todays-lesson");
      if (response.status === 200) {
        setTodaysLesson(response.data?.lesson);
      }
    } catch (error) {
      console.log("Error getting today's lesson", error);
    }
  };

  useEffect(() => {
    getTodaysLesson();
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post("/courselessons/enroll", {
        startingLessonNumber: startingLesson,
      });
      if (response.status === 200) {
        setSuccess(true);
        setDialogState(false);
        getTodaysLesson();
      }
    } catch (error) {
      console.log("Error enrolling", error);
      setErrorState({
        error: true,
        message: ERROR_ENROLL_MESSAGE,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async () => {
    try {
      const response = await axiosInstance.put("user/update", {
        showAcimLesson: lessonOnDashboard,
      });
      if (response.status === 200) {
        dispatch(getUserById());
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    updateUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonOnDashboard, dispatch]);

  return (
    <RootBox>
      <ConfirmationDialog
        open={dialogState}
        handleClose={() => setDialogState(false)}
        title={DIALOG_TITLE}
        content={DIALOG_CONTENT}
        onSave={handleSave}
        startingLesson={startingLesson}
        setStartingLesson={setStartingLesson}
        todaysLesson={todaysLesson}
      />

      <HeaderStack
        $hasTodaysLesson={!!todaysLesson}
        direction="row"
        spacing={2}
      >
        <Heading>{SECTION_HEADING}</Heading>
        <AccessLink onClick={() => setDialogState(true)}>
          {ACCESS_LINK_TEXT}
        </AccessLink>
      </HeaderStack>

      {todaysLesson && (
        <CheckboxWrapper
          control={
            <StyledCheckbox
              checked={lessonOnDashboard}
              onChange={() => setLessonOnDashboard(!lessonOnDashboard)}
              icon={<CheckboxIconUnchecked />}
              checkedIcon={<CheckboxIconChecked />}
            />
          }
          label={<CheckboxLabel>{CHECKBOX_LABEL}</CheckboxLabel>}
        />
      )}

      <ContentGrid>
        <LessonSidebar
          lessonsData={lessonsData}
          setLessonContent={setLessonContent}
        />

        <ContentBox>
          <ContentText dangerouslySetInnerHTML={{ __html: lessonContent }} />
        </ContentBox>
      </ContentGrid>

      <StyledBackdrop open={loading}>
        <CircularProgress color="inherit" />
      </StyledBackdrop>

      <ErrorSnackbar
        error={errorState.error}
        handleCloseError={() => setErrorState({ error: false, message: "" })}
        errorMsg={errorState.message}
      />
      <ConfirmationSnackbar
        snackbarState={success}
        handleSnackbarClose={() => setSuccess(false)}
        text={SUCCESS_MESSAGE}
      />
    </RootBox>
  );
};

export default LessonDashboard;
